import React from 'react';
import PropTypes from 'prop-types';

import {Stack, Box, Typography, LinearProgress} from 'views/components';
import {CalendarToday, AccessTime, CheckCircleOutlined, TimerOutlined, CreditCardOutlined, LocalAtm} from 'views/icons';
import {Translate, translate} from 'views/module';

import StatCard from './StatCard';

import moment from 'moment';
import WorkHourCalculator from 'utils/work_hour/work_hour_calculator';

const SlipHourOverview = (props) => {
  const {slipDetail, caregiverPaymentPlan} = props;

  const hours = slipDetail.hours;
  const totalShifts = hours.length;

  const totalHours = hours.reduce((sum, shift) => {
    const start = moment(shift.eventData.startTime);
    const end = moment(shift.eventData.endTime);
    let breakDuration = 0;

    if (shift?.event && shift?.event.type ==='LIVEIN_EVENT') {
      return sum + 8;
    }
    if (shift.event && shift.event.breaks) {
      breakDuration = (shift.event.breaks.breakDuration * shift.event.breaks.breakCount) / 60;
    }

    return sum + moment.duration(end.diff(start)).asHours() - breakDuration;
  }, 0);

  const workedShifts = hours.filter((shift) => {
    const clockInUpdate = shift.event?.eventUpdateDatas?.find((update) => update.type === 'clockInConfirmation');
    const clockOutUpdate = shift.event?.eventUpdateDatas?.find((update) => update.type === 'clockOutConfirmation');

    if (!clockInUpdate || !clockOutUpdate) {
      return false;
    }

    const clockInTime = moment(clockInUpdate.data.suggestedClockinTimeFormatted);
    const shiftEndTime = moment(shift.eventData.endTime);

    // Don't count shift if clock in time is after shift end
    if (clockInTime.isAfter(shiftEndTime)) {
      return false;
    }

    return true;
  }).length;

  const {workedHours, totalExpenses} = hours.reduce((acc, shift) => {
    const clockInUpdate = shift.event?.eventUpdateDatas?.find((update) => update.type === 'clockInConfirmation');
    const clockOutUpdate = shift.event?.eventUpdateDatas?.find((update) => update.type === 'clockOutConfirmation');

    let hoursToAdd = 0;
    let expensesToAdd = 0;
    let breakDuration = 0;

    if (clockInUpdate && clockOutUpdate) {
      const scheduledStart = shift.eventData.startTime;
      const scheduledEnd = shift.eventData.endTime;

      const actualIn = clockInUpdate.data.suggestedClockinTimeFormatted;
      const actualOut = clockOutUpdate.data.suggestedClockoutTimeFormatted;

      const {hoursWorked} = WorkHourCalculator.calculateWorkedHours({
        scheduleStartIso: scheduledStart,
        scheduleEndIso: scheduledEnd,
        actualInIso: actualIn,
        actualOutIso: actualOut,
      });


      if (shift.event?.breaks) {
        breakDuration = (shift.event.breaks.breakDuration * shift.event.breaks.breakCount) / 60;
      }

      const duration = hoursWorked;
      const roundedDuration = Number(duration.toFixed(2));
      hoursToAdd = roundedDuration - breakDuration;
    } else if (shift.event && shift.event.type ==='LIVEIN_EVENT') {
      hoursToAdd = 8;
    }
    // Sum expenses if the event has an expense array with items
    if (shift.expenses && shift.expenses.length > 0) {
      expensesToAdd = shift.expenses.reduce((expenseSum, expense) => {
        return expenseSum + (parseFloat(expense.price));
      }, 0);
    }

    return {
      workedHours: acc.workedHours + hoursToAdd,
      totalExpenses: acc.totalExpenses + expensesToAdd,
    };
  }, {workedHours: 0, totalExpenses: 0});
  const hourlyRate = caregiverPaymentPlan?.hourlyRate ? parseFloat(caregiverPaymentPlan.hourlyRate) : null;
  const totalPay = hourlyRate ? workedHours * hourlyRate : 0;

  // Calculate completion percentage and total earned
  const completionPercentage = totalShifts > 0 ? (workedShifts / totalShifts) * 100 : 0;
  const totalEarned = totalPay + totalExpenses;

  return (
    <Stack sx={{p: 2}} spacing={3}>
      <Typography sx={{fontWeight: 'bold', fontSize: '1.2rem'}}>
        <Translate expression='caregiver.CaregiverDetail.Payment.WorkSummary.Title' />
      </Typography>

      {/* First Row - Scheduled Work */}
      <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
        <StatCard
          title={translate('caregiver.CaregiverDetail.Payment.WorkSummary.ShiftsScheduled')}
          value={totalShifts}
          icon={<CalendarToday sx={{fontSize: '2rem'}} />}
          bgColor="#f0f7ff"
          titleColor="#5268a6"
          valueColor="#1e3a8a"
        />
        <StatCard
          title={translate('caregiver.CaregiverDetail.Payment.WorkSummary.HoursScheduled')}
          icon={<AccessTime sx={{fontSize: '2rem'}} />}
          bgColor="#fffbf0"
          titleColor="#9d6b4c"
          valueColor="#92400e"
        >
          <Translate expression='caregiver.CaregiverDetail.Payment.HoursDisplay' substitute={{hours: totalHours.toFixed(2)}} />
        </StatCard>
      </Box>

      {/* Second Row - Completed Work */}
      <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
        <StatCard
          title={translate('caregiver.CaregiverDetail.Payment.WorkSummary.ShiftsWorked')}
          value={workedShifts}
          icon={<CheckCircleOutlined sx={{fontSize: '2rem'}} />}
          bgColor="#f0f8f0"
          titleColor="#407869"
          valueColor="#064e3b"
        />
        <StatCard
          title={translate('caregiver.CaregiverDetail.Payment.WorkSummary.HoursWorkedTitle')}
          icon={<TimerOutlined sx={{fontSize: '2rem'}} />}
          bgColor="#f8f0f8"
          titleColor="#8558a8"
          valueColor="#581c87"
        >
          <Translate expression='caregiver.CaregiverDetail.Payment.HoursDisplay' substitute={{hours: workedHours.toFixed(2)}} />
        </StatCard>
      </Box>

      {/* Third Row - Earnings */}
      <Box sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2}}>
        {
          hourlyRate && hourlyRate > 0 &&
          <StatCard
            title={`${translate('caregiver.CaregiverDetail.Payment.WorkSummary.TotalPay')} @ $${hourlyRate.toFixed(2)}/hr`}
            icon={<CreditCardOutlined sx={{fontSize: '2rem'}} />}
            bgColor="#fff8f0"
            titleColor="#a26953"
            valueColor="#92400e"
          >
            <Translate expression='caregiver.CaregiverDetail.Payment.CostDisplay' substitute={{cost: totalPay.toFixed(2)}} />
          </StatCard>
        }
        {
          totalExpenses > 0 &&
          <StatCard
            title={translate('caregiver.CaregiverDetail.Payment.WorkSummary.TotalExpenses')}
            icon={<LocalAtm sx={{fontSize: '2rem'}} />}
            bgColor="#fff0f0"
            titleColor="#a25353ff"
            valueColor="#991b1b"
          >
            <Translate expression='caregiver.CaregiverDetail.Payment.CostDisplay' substitute={{cost: totalExpenses.toFixed(2)}} />
          </StatCard>
        }
      </Box>

      {/* Weekly Progress Bar */}
      <Box
        sx={{
          backgroundColor: '#fafafa',
          borderRadius: '8px',
          padding: '16px',
          border: '1px solid #f0f0f0',
        }}
      >
        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <Typography sx={{fontWeight: 'bold', fontSize: '1.1rem', color: '#272727ff'}}>
            <Translate expression='caregiver.CaregiverDetail.Payment.WorkSummary.WeeklyProgress.Title' />
          </Typography>
          <Typography sx={{fontWeight: 'bold', fontSize: '1.5rem', color: '#272727ff'}}>
            ${totalEarned.toFixed(2)}
          </Typography>
        </Box>

        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
          <Typography sx={{fontSize: '0.875rem', color: '#777'}}>
            <Translate
              expression='caregiver.CaregiverDetail.Payment.WorkSummary.WeeklyProgress.ShiftsCompletedWithRate'
              substitute={{
                workedShifts: workedShifts,
                totalShifts: totalShifts,
                percentage: completionPercentage.toFixed(0),
              }}
            />
          </Typography>
          <Typography sx={{fontSize: '0.875rem', color: '#777'}}>
            <Translate expression='caregiver.CaregiverDetail.Payment.WorkSummary.WeeklyProgress.Earned' />
          </Typography>
        </Box>

        <LinearProgress
          variant="determinate"
          value={completionPercentage}
          sx={{
            'height': 8,
            'borderRadius': 4,
            'backgroundColor': '#f0f0f0',
            '& .MuiLinearProgress-bar': {
              backgroundColor: completionPercentage === 100 ? '#7fc57f' : '#6db4d4',
              borderRadius: 4,
            },
          }}
        />
      </Box>
    </Stack>
  );
};

SlipHourOverview.propTypes = {
  slipDetail: PropTypes.object.isRequired,
  caregiverPaymentPlan: PropTypes.object.isRequired,
};

export default React.memo(SlipHourOverview);
